import React, { ReactNode, useContext } from "react";
import { useSelector } from "react-redux";

import { Annotation } from "@com.mgmtp.a12.base/base-model-api/lib/main/header";
import { Activity, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DefaultFormModelMap, FormModelMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { ExternalLink } from "@com.mgmtp.a12.widgets/widgets-core/lib/link";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { ActivityIdContext } from "../../../component/ActivityContext";
import { PermissionDataholder } from "../../../types/permission";
import { assertNonNullable } from "../../../utilities/assert";
import { entityInstancePathFromA12Path } from "../../../utilities/pathHelpers";
import { ModulfDataSelectors } from "../../../utilities/selectors/modulfDataSelectors";
import {
	INFOBOX,
	URL_DISPLAY,
	URL_DISPLAY_HIDE_IN_READONLY,
	URL_DISPLAY_LABEL
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { PERMISSON_DATAHOLDER } from "../../processOverview/store/constants";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";

const documentService = new DocumentServiceFactory().getDocumentService();

export function CustomTextCell(props: FormModelMap.FormModelComponentProps<FormModel.TextCell>): ReactNode {
	const { annotations } = props.modelElement;
	const taskActivity = useSelector(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	if (isUrlDisplay(annotations)) {
		return <CustomUrlTextCell {...props} />;
	}
	if (isInfoForUnpermittedUser(annotations) && isUserPermittedForTask(taskActivity)) {
		return <></>;
	}
	return <DefaultFormModelMap.TextCell.component {...props} />;
}

function isInfoForUnpermittedUser(annotation: readonly Annotation[] | undefined): boolean {
	return !!annotation?.find(e => e.name === INFOBOX);
}

function isUserPermittedForTask(taskActivity: Activity | undefined): boolean {
	const permissionDataholder = taskActivity?.dataHolders?.find(
		dh => dh.descriptor?.[PERMISSON_DATAHOLDER] === PERMISSON_DATAHOLDER
	) as Activity.DataHolder<PermissionDataholder> | undefined;
	return permissionDataholder?.data?.isUserPermittedForTask ?? true;
}

function isUrlDisplay(annotation: readonly Annotation[] | undefined): boolean {
	return !!annotation?.find(e => e.name === URL_DISPLAY);
}

const CustomUrlTextCell: React.ComponentType<FormModelMap.FormModelComponentProps<FormModel.TextCell>> =
	function CustomUrlTextCell(props) {
		const { annotations } = props.modelElement;
		const activityId = useContext(ActivityIdContext);
		const isReadonly = useSelector(ModulfUiSelectors.selectIsReadonly(activityId));
		assertNonNullable(activityId);
		const data = useSelector(ModulfDataSelectors.selectTaskDocument(activityId));
		const path = annotations?.find(e => e.name === URL_DISPLAY)?.value;
		if (!(data && path)) {
			return null;
		}

		const relativePath = path.substring(path.indexOf("/root/"));
		const entityInstancePath = entityInstancePathFromA12Path(relativePath);
		const fieldValue = documentService.getAssignedObject(data, entityInstancePath);
		const hiddenInReadonly = !!annotations?.find(e => e.name === URL_DISPLAY_HIDE_IN_READONLY);
		const shouldHide = hiddenInReadonly && isReadonly;
		if (typeof fieldValue === "string" && !shouldHide) {
			const label = annotations?.find(e => e.name === URL_DISPLAY_LABEL)?.value ?? fieldValue;
			return (
				<ExternalLink title={fieldValue} href={fieldValue}>
					{label}
				</ExternalLink>
			);
		}

		return null;
	};
